import React, { useMemo, useState } from "react";
import { Megaphone, ClipboardCheck, CalendarDays, FileText, ImageIcon, Flag, BookOpen } from "lucide-react";
import { Panel, EmptyState } from "@/portal/ui/Layout";
import { Segmented } from "@/portal/ui/Tabs";
import { DECISION_STATUS } from "@/portal/ui/Badge";
import { fmtDateLong, fmtDateTime, fmtMonth } from "@/portal/format";

const TYPES = {
  update: { label: "Actualizare", icon: Megaphone, tone: "" },
  decision: { label: "Decizie", icon: ClipboardCheck, tone: "amber" },
  meeting: { label: "Ședință", icon: CalendarDays, tone: "" },
  document: { label: "Document", icon: FileText, tone: "neutral" },
  photo: { label: "Fotografii", icon: ImageIcon, tone: "neutral" },
  milestone: { label: "Reper", icon: Flag, tone: "success" },
};

/** Jurnalul proiectului: cronologie unificată a evenimentelor publicate. */
export default function JournalTab({ portal, documents, photos, milestones }) {
  const [filter, setFilter] = useState("all");
  const events = useMemo(() => buildEvents(portal, documents, photos, milestones), [portal, documents, photos, milestones]);
  const counts = events.reduce((a, e) => { a[e.type] = (a[e.type] || 0) + 1; return a; }, {});
  const list = events.filter((e) => filter === "all" || e.type === filter);

  if (events.length === 0) return <EmptyState icon={BookOpen} title="Jurnalul proiectului este gol" description="Aici se adună, cronologic, actualizările, deciziile, ședințele și documentele publicate de echipă." testid="journal-empty" />;

  const groups = [];
  list.forEach((e) => { const k = e.at.slice(0, 7); let g = groups.find((x) => x.k === k); if (!g) { g = { k, items: [] }; groups.push(g); } g.items.push(e); });

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
        <Segmented ariaLabel="Tip eveniment" value={filter} onChange={setFilter}
          options={[{ value: "all", label: "Toate", count: events.length }, ...Object.entries(TYPES).filter(([k]) => counts[k]).map(([k, t]) => ({ value: k, label: t.label, count: counts[k] }))]} />
      </div>
      <div className="space-y-8" data-testid="journal">
        {groups.map((g) => (
          <section key={g.k} aria-label={fmtMonth(g.k)}>
            <h3 className="cp-label mb-3">{fmtMonth(g.k)}</h3>
            <Panel padded={false}>
              <ol className="divide-y divide-cp-border">
                {g.items.map((e) => {
                  const t = TYPES[e.type]; const Icon = t.icon;
                  return (
                    <li key={e.id} className="flex items-start gap-4 px-5 py-4" data-testid={`journal-${e.id}`}>
                      <span className={`cp-tile is-sm ${t.tone ? `is-${t.tone}` : ""} mt-0.5`} aria-hidden="true"><Icon /></span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap"><span className="text-[14px] font-semibold text-cp-text">{e.title}</span><span className="cp-meta">· {t.label}</span></div>
                        {e.body && <p className="text-[13.5px] text-cp-text-2 mt-0.5 leading-relaxed">{e.body}</p>}
                        <div className="cp-meta mt-1">{e.time ? fmtDateTime(e.at) : fmtDateLong(e.at)}{e.by ? ` · ${e.by}` : ""}</div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </Panel>
          </section>
        ))}
      </div>
    </div>
  );
}

function buildEvents(portal, documents = [], photos = [], milestones = []) {
  const ev = [];
  (portal?.updates || []).forEach((u) => ev.push({ id: `u-${u.id}`, type: "update", at: u.at, time: true, by: u.by, title: "Actualizare de la echipă", body: u.progress_this_week || u.next_steps || u.client_actions }));
  (portal?.actions || []).forEach((a) => {
    ev.push({ id: `a-${a.id}`, type: "decision", at: a.created_at, time: true, by: a.created_by, title: `Solicitare transmisă: ${a.title}`, body: [a.cost_impact && `Impact cost ${a.cost_impact}`, a.schedule_impact && `impact program ${a.schedule_impact}`].filter(Boolean).join(" · ") });
    (a.log || []).forEach((l) => ev.push({ id: `l-${a.id}-${l.version}`, type: "decision", at: l.when, time: true, by: l.who, title: `${DECISION_STATUS[l.what]?.label || l.what}: ${a.title}`, body: l.comment ? `„${l.comment}”` : "" }));
  });
  (portal?.meetings || []).forEach((m) => m.when && ev.push({ id: `m-${m.id}`, type: "meeting", at: m.when, time: true, title: m.title, body: m.minutes || m.agenda || "" }));
  documents.forEach((d) => ev.push({ id: `d-${d.id}`, type: "document", at: d.uploaded_at, by: d.uploaded_by, title: `Document publicat: ${d.name}` }));
  const byMonth = {};
  photos.forEach((p) => { const k = (p.uploaded_at || "").slice(0, 10) || p.month; if (!k) return; byMonth[k] = byMonth[k] || { n: 0, at: p.uploaded_at || `${p.month}-01` }; byMonth[k].n += 1; });
  Object.entries(byMonth).forEach(([k, v]) => ev.push({ id: `p-${k}`, type: "photo", at: v.at, title: `${v.n} ${v.n === 1 ? "fotografie publicată" : "fotografii publicate"} în galerie` }));
  milestones.filter((m) => m.completed).forEach((m) => ev.push({ id: `ms-${m.id}`, type: "milestone", at: m.completed_at || m.due_date, title: `Reper finalizat: ${m.title}` }));
  return ev.filter((e) => e.at).sort((a, b) => String(b.at).localeCompare(String(a.at)));
}
